/**
 * createCrudMutation Factory
 * 
 * Factory function for creating mutations with automatic
 * - Query invalidation
 * - Toast notifications
 * - Error handling
 * - Callback support
 * 
 * @example
 * const createMutation = createCrudMutation({
 *   mutationFn: (data) => userService.create(data),
 *   queryKey: 'users',
 *   successMessage: { title: 'Criado!', message: 'Usuário criado com sucesso' },
 *   errorMessage: 'Erro ao criar usuário',
 *   onSuccess: () => setIsModalOpen(false),
 *   queryClient,
 *   toast,
 * });
 */

import { useMutation } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

export interface CreateCrudMutationConfig<TData = any, TError = Error> {
  mutationFn: (data: any) => Promise<TData>;
  queryKey: string;
  successMessage?: { title: string; message?: string };
  errorMessage?: string;
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  queryClient: QueryClient;
  toast: any; // useToast hook return
  additionalQueryKeys?: string[];
}

/**
 * Create a mutation with automatic success/error handling
 */
export const createCrudMutation = <TData = any, TError = Error>({
  mutationFn,
  queryKey,
  successMessage,
  errorMessage = 'Erro na operação',
  onSuccess,
  onError,
  queryClient,
  toast,
  additionalQueryKeys = [],
}: CreateCrudMutationConfig<TData, TError>) => {
  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      // Invalidate main query key
      queryClient.invalidateQueries({ queryKey: [queryKey] });

      // Invalidate additional query keys if provided
      additionalQueryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });

      // Show success toast
      if (successMessage) {
        toast.success(successMessage.title, successMessage.message);
      }

      // Call callback
      onSuccess?.(data);
    },
    onError: (error: any) => {
      // Show error toast
      const message = error?.message || errorMessage;
      toast.error('Erro', message);

      // Call callback
      onError?.(error);
    },
  });
};
